import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  ChartNoAxesColumn,
  Database,
  Funnel,
  Image,
  Layers,
  Megaphone,
  MousePointerClick,
  Upload,
} from "lucide-react";

import { queryClient } from "App";
import ACL_RELATIONSHIPS from "acl-relationships";
import AccessDenied from "components/AccessDenied";
import LeadAccountSwitcher from "components/leads/LeadAccountSwitcher";
import MetaLastSynced from "components/MetaLastSynced";
import MetaSyncButton from "components/MetaSyncButton";
import SidebarDetailLayout from "components/SidebarDetailLayout";
import { useAppTabs } from "hooks/useAppTabs";
import useAuthorized from "hooks/useAuthorized";
import usePageTitle from "hooks/usePageTitle";
import MetaAdAccountService from "services/MetaAdAccount";
import AccountLeadsPage from "routes/meta_leads/AccountLeadsPage";
import HealthCheckTab from "routes/meta_leads/HealthCheckTab";
import ImportLeadsTab from "routes/meta_leads/ImportLeadsTab";
import MetaAdSetsTab from "routes/meta_leads/MetaAdSetsTab";
import MetaAdsTab from "routes/meta_leads/MetaAdsTab";
import MetaCampaignsTab from "routes/meta_leads/MetaCampaignsTab";
import MetaDatasetsTab from "routes/meta_leads/MetaDatasetsTab";
import MetaEventsTab from "routes/meta_leads/MetaEventsTab";
import MetaInsightsTab from "routes/meta_leads/MetaInsightsTab";
import MetaSandboxAdSetsTab from "routes/meta_leads/MetaSandboxAdSetsTab";
import MetaSandboxAdsTab from "routes/meta_leads/MetaSandboxAdsTab";
import MetaSandboxCampaignsTab from "routes/meta_leads/MetaSandboxCampaignsTab";

const SECTION_ACLS = [
  ACL_RELATIONSHIPS.metaAdAccount.readLeads,
  ACL_RELATIONSHIPS.metaAdAccount.readCampaigns,
  ACL_RELATIONSHIPS.metaAdAccount.readAdSets,
  ACL_RELATIONSHIPS.metaAdAccount.readAds,
  ACL_RELATIONSHIPS.metaAdAccount.readInsights,
  ACL_RELATIONSHIPS.metaAdAccount.readPixelStats,
  ACL_RELATIONSHIPS.metaAdAccount.readDatasets,
  ACL_RELATIONSHIPS.metaAdAccount.readSandbox,
  ACL_RELATIONSHIPS.metaAdAccount.importLeads,
  ACL_RELATIONSHIPS.metaAdAccount.sendTestEvent,
];

const MetaAccountShell = () => {
  const { accountId } = useParams();
  const tabs = useAppTabs({
    basePath: `/leads/meta/${accountId}`,
    defaultTab: "leads",
  });
  const { grantedInfo } = useAuthorized(SECTION_ACLS, {
    shouldReturnGrantedInfo: true,
  });
  const [
    canReadLeads,
    canReadCampaigns,
    canReadAdSets,
    canReadAds,
    canReadInsights,
    canReadPixelStats,
    canReadDatasets,
    canReadSandbox,
    canImportLeads,
    canHealthCheck,
  ] = grantedInfo.map(info => info?.granted);

  const { data: account } = useQuery({
    queryKey: ["meta-ad-account", accountId],
    queryFn: () => MetaAdAccountService.get(accountId),
    enabled:
      canReadLeads ||
      canReadCampaigns ||
      canReadAdSets ||
      canReadAds ||
      canReadInsights ||
      canReadPixelStats ||
      canReadDatasets ||
      canReadSandbox ||
      canImportLeads ||
      canHealthCheck,
    // While either group is mid-sync, poll so the rail (and, through the shared
    // cache, the section tables) update automatically as each domain lands.
    refetchInterval: query => {
      const sync = query.state.data?.sync;
      return sync?.live?.status === "pending" ||
        sync?.sandbox?.status === "pending"
        ? 2000
        : false;
    },
  });

  usePageTitle(account?.name || "Meta Account");

  const sections = useMemo(
    () => [
      {
        key: "leads",
        label: "All Leads",
        icon: Funnel,
        group: "Leads",
        content: <AccountLeadsPage />,
        hidden: !canReadLeads,
      },
      {
        key: "campaigns",
        label: "Campaigns",
        icon: Megaphone,
        group: "Business",
        content: <MetaCampaignsTab />,
        hidden: !canReadCampaigns,
      },
      {
        key: "adsets",
        label: "Ad Sets",
        icon: Layers,
        group: "Business",
        content: <MetaAdSetsTab />,
        hidden: !canReadAdSets,
      },
      {
        key: "ads",
        label: "Ads",
        icon: Image,
        group: "Business",
        content: <MetaAdsTab />,
        hidden: !canReadAds,
      },
      {
        key: "insights",
        label: "Insights",
        icon: ChartNoAxesColumn,
        group: "Business",
        content: <MetaInsightsTab />,
        hidden: !canReadInsights,
      },
      {
        key: "events",
        label: "Events",
        icon: MousePointerClick,
        group: "Business",
        content: <MetaEventsTab />,
        hidden: !canReadPixelStats,
      },
      {
        key: "sandbox-campaigns",
        label: "Campaigns",
        icon: Megaphone,
        group: "Sandbox",
        content: <MetaSandboxCampaignsTab />,
        // Sandbox section is hidden for now.
        hidden: true,
      },
      {
        key: "sandbox-adsets",
        label: "Ad Sets",
        icon: Layers,
        group: "Sandbox",
        content: <MetaSandboxAdSetsTab />,
        hidden: true,
      },
      {
        key: "sandbox-ads",
        label: "Ads",
        icon: Image,
        group: "Sandbox",
        content: <MetaSandboxAdsTab />,
        hidden: true,
      },
      {
        key: "datasets",
        label: "Datasets",
        icon: Database,
        group: "Datasets",
        content: <MetaDatasetsTab />,
        hidden: !canReadDatasets,
      },
      {
        key: "import-leads",
        label: "Import leads",
        icon: Upload,
        group: "Import",
        content: <ImportLeadsTab />,
        hidden: !canImportLeads,
      },
      {
        key: "health-check",
        label: "Health check",
        icon: Activity,
        group: "Health Check",
        content: <HealthCheckTab />,
        hidden: !canHealthCheck,
      },
    ],
    [
      canReadLeads,
      canReadCampaigns,
      canReadAdSets,
      canReadAds,
      canReadInsights,
      canReadPixelStats,
      canReadDatasets,
      canReadSandbox,
      canImportLeads,
      canHealthCheck,
    ]
  );

  // Pick up the backend `pending` status set at dispatch; once it lands, the
  // account query's refetchInterval takes over and observes completion. The
  // tables refresh themselves off the same status, so no table invalidation is
  // needed here.
  const onSynced = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["meta-ad-account", accountId],
    });
  }, [accountId]);

  if (sections.every(section => section.hidden)) {
    return (
      <AccessDenied description="You don't have permission to view this account." />
    );
  }

  const businessAction = (
    <MetaSyncButton
      onSync={() => MetaAdAccountService.syncAll(accountId)}
      onSynced={onSynced}
      isSyncing={account?.sync?.live?.status === "pending"}
      disabled={!account?.is_sync_enabled}
    />
  );
  const businessNote = (
    <MetaLastSynced
      lastSyncedAt={account?.sync?.live?.last_synced_at}
      status={account?.sync?.live?.status}
      domains={account?.sync?.live?.domains}
    />
  );

  const sandboxAction = (
    <MetaSyncButton
      onSync={() => MetaAdAccountService.receiveSandbox(accountId)}
      onSynced={onSynced}
      isSyncing={account?.sync?.sandbox?.status === "pending"}
    />
  );
  const sandboxNote = (
    <MetaLastSynced
      lastSyncedAt={account?.sync?.sandbox?.last_synced_at}
      status={account?.sync?.sandbox?.status}
      domains={account?.sync?.sandbox?.domains}
    />
  );

  const decoratedSections = sections.map(section => {
    if (
      section.group === "Leads" ||
      section.group === "Import" ||
      section.group === "Datasets" ||
      section.group === "Health Check"
    ) {
      return { ...section };
    }
    if (section.group === "Sandbox") {
      return { ...section, groupAction: sandboxAction, groupNote: sandboxNote };
    }
    return { ...section, groupAction: businessAction, groupNote: businessNote };
  });

  return (
    <SidebarDetailLayout
      sections={decoratedSections}
      tabs={tabs}
      header={<LeadAccountSwitcher />}
      mobileMenuLabel="Leads Navigation"
    />
  );
};

export default MetaAccountShell;
